// Higher rank shows first. Unplaced rows (null) sort after placed ones by request
// count, which is also where Zero puts nulls in a descending sort.

export const RANK_STEP = 1024
// Doubles run out of room after ~50 halvings of one gap; renumber well before.
const MIN_GAP = 1e-6
export const MAX_RANK_UPDATES = 1000
export const MAX_GROUP_ORDER_KEYS = 200

export interface RankedRow {
	id: string
	rank: number | null | undefined
}

export interface RankUpdate {
	id: string
	portalSortOrder: number
}

export function assertRankUpdates(
	orders: readonly RankUpdate[],
	role: string,
): void {
	if (role === "VIEWER") throw new Error("Viewers cannot reorder the roadmap")
	if (orders.length > MAX_RANK_UPDATES) {
		throw new Error(`Reorder at most ${MAX_RANK_UPDATES} rows at once`)
	}
	for (const order of orders) {
		if (!order.id || !Number.isFinite(order.portalSortOrder)) {
			throw new Error("Every reordered row needs an id and a finite rank")
		}
	}
}

export function normalizeRoadmapGroupKey(key: string): string {
	return key.trim().toLowerCase()
}

/** The key the portal groups by: a custom status name, else the status type. */
export function roadmapGroupKey(
	status: string | null | undefined,
	statusName: string | null | undefined,
): string {
	return normalizeRoadmapGroupKey(statusName?.trim() || status || "")
}

export function compareCustomOrder(
	a: { id: string; rank: number | null | undefined; score: number },
	b: { id: string; rank: number | null | undefined; score: number },
): number {
	const rankA = a.rank ?? null
	const rankB = b.rank ?? null
	if (rankA !== rankB) {
		if (rankA === null) return 1
		if (rankB === null) return -1
		return rankB - rankA
	}
	if (a.score !== b.score) return b.score - a.score
	return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
}

export function sortByCustomOrder<T extends { id: string }>(
	items: readonly T[],
	rankOf: (item: T) => number | null | undefined,
	scoreOf: (item: T) => number,
): T[] {
	return [...items].sort((a, b) =>
		compareCustomOrder(
			{ id: a.id, rank: rankOf(a), score: scoreOf(a) },
			{ id: b.id, rank: rankOf(b), score: scoreOf(b) },
		),
	)
}

/** Groups named in `groupOrder` first, in that order; the rest keep theirs. */
export function orderGroupsByCustom<T>(
	groups: readonly T[],
	keyOf: (group: T) => string,
	groupOrder: readonly string[],
): T[] {
	const position = new Map<string, number>()
	groupOrder.forEach((key, index) => {
		const normalized = normalizeRoadmapGroupKey(key)
		if (!position.has(normalized)) position.set(normalized, index)
	})
	return groups
		.map((group, index) => ({
			group,
			index,
			position:
				position.get(normalizeRoadmapGroupKey(keyOf(group))) ?? Infinity,
		}))
		.sort((a, b) =>
			a.position === b.position ? a.index - b.index : a.position - b.position,
		)
		.map(({ group }) => group)
}

/** Groups not on screen keep their place after the visible ones. */
export function nextGroupOrder(
	stored: readonly string[],
	visibleInNewOrder: readonly string[],
): string[] {
	const result: string[] = []
	const seen = new Set<string>()
	for (const key of [...visibleInNewOrder, ...stored]) {
		const normalized = normalizeRoadmapGroupKey(key)
		if (!normalized || seen.has(normalized)) continue
		seen.add(normalized)
		result.push(normalized)
	}
	return result.slice(0, MAX_GROUP_ORDER_KEYS)
}

// `toIndex` counts the list without the moved row. Dropping below unplaced rows
// places them too, since nulls sort last; below the last loaded row is a guess.
export function planCustomRanks(
	rows: readonly RankedRow[],
	movedId: string,
	toIndex: number,
): RankUpdate[] {
	const moved = rows.find((row) => row.id === movedId)
	if (!moved) return []
	const rest = rows.filter((row) => row.id !== movedId)
	const index = Math.max(0, Math.min(toIndex, rest.length))
	const next = [...rest.slice(0, index), moved, ...rest.slice(index)]

	let start = index
	while (start > 0 && (next[start - 1]?.rank ?? null) === null) start--

	const top = start > 0 ? (next[start - 1]?.rank ?? null) : null
	const bottom = next[index + 1]?.rank ?? null
	const count = index - start + 1

	let ranks: number[]
	if (top !== null && bottom !== null) {
		const gap = (top - bottom) / (count + 1)
		if (!(gap > MIN_GAP)) return renumber(next, movedId, index)
		ranks = Array.from({ length: count }, (_, k) => top - gap * (k + 1))
	} else if (top !== null) {
		ranks = Array.from({ length: count }, (_, k) => top - RANK_STEP * (k + 1))
	} else if (bottom !== null) {
		ranks = Array.from(
			{ length: count },
			(_, k) => bottom + RANK_STEP * (count - k),
		)
	} else {
		ranks = Array.from({ length: count }, (_, k) => -RANK_STEP * k)
	}

	return ranks
		.map((rank, k) => ({ row: next[start + k]!, rank }))
		.filter(({ row, rank }) => row.id === movedId || row.rank !== rank)
		.map(({ row, rank }) => ({ id: row.id, portalSortOrder: rank }))
}

// The gap is spent: spread every placed row and the moved row out again, with
// the lowest one keeping its rank so rows below the loaded head stay below.
function renumber(
	next: readonly RankedRow[],
	movedId: string,
	index: number,
): RankUpdate[] {
	let last = index
	for (let j = next.length - 1; j > index; j--) {
		if ((next[j]?.rank ?? null) !== null) {
			last = j
			break
		}
	}
	const placed = next
		.slice(0, last + 1)
		.filter((row) => row.id !== movedId && row.rank != null)
		.map((row) => row.rank!)
	const floor = placed.length > 0 ? Math.min(...placed) : 0
	return next.slice(0, last + 1).map((row, j) => ({
		id: row.id,
		portalSortOrder: floor + (last - j) * RANK_STEP,
	}))
}
